// Columns of the charter request's quotes table (aircraft, route, times, money, status, booking, actions).
import type { NavigateFunction } from 'react-router-dom';
import { Button, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import type { CharterQuote } from '../../api/types';
import { airportLabel, formatCategory } from '../../data/airportLabel';
import { formatHoursMinutes } from '../../data/duration';
import { COMMISSION_LABEL, gbp, amount } from '../../data/format';
import { fmtZoned } from '../../data/airportTime';
import { CHARTER_QUOTE_STATUS_COLORS, CHARTER_QUOTE_STATUS_LABELS, statusLabel } from '../../data/statuses';
import { alternativeLabels } from '../../data/quoteAlternatives';

const { Text } = Typography;

export function quoteColumns({
  nav,
  openEdit,
  deleteMut,
}: {
  nav: NavigateFunction;
  openEdit: (q: CharterQuote) => void;
  deleteMut: { mutate: (qid: string) => void };
}): ColumnsType<CharterQuote> {
  return [
    { title: 'Tail', dataIndex: 'tailNumber', key: 'tail', width: 100, fixed: 'left', render: (t) => t || '—' },
    // SHAR-608 — the operator behind the quoted aircraft.
    { title: 'Operator', key: 'op', width: 150, ellipsis: true, render: (_, q) => q.operatorName || '—' },
    { title: 'Model', key: 'model', width: 150, ellipsis: true, render: (_, q) => q.modelName || '—' },
    { title: 'Category', key: 'cat', width: 130, render: (_, q) => (q.category ? formatCategory(q.category) : '—') },
    { title: 'Seats', key: 'seats', width: 70, render: (_, q) => q.seatingCapacity ?? '—' },
    { title: 'Year', key: 'yom', width: 80, render: (_, q) => q.yearOfManufacture ?? '—' },
    { title: 'Refurb', key: 'yor', width: 90, render: (_, q) => q.yearOfRefurbishment ?? '—' },
    {
      // SHAR-607 — this showed the bare stored code, which is IATA, while
      // the rest of the product speaks ICAO. Now ICAO with city and
      // country, resolved server-side so every screen agrees.
      title: 'Route',
      key: 'route',
      width: 260,
      render: (_, q) => (
        <span>
          {airportLabel(q.originCode, q.originCity, q.originCountry)}
          {' → '}
          {airportLabel(q.destCode, q.destCity, q.destCountry)}
        </span>
      ),
    },
    // SHAR-614 — what the customer will see marked as "Alternative ...".
    {
      title: 'Differs from request',
      key: 'alternatives',
      width: 190,
      render: (_, q) => {
        const labels = alternativeLabels(q.alternatives);
        return labels.length ? (
          <Space size={4} wrap>
            {labels.map((l) => (
              <Tag key={l} color="gold">
                {l}
              </Tag>
            ))}
          </Space>
        ) : (
          '—'
        );
      },
    },
    // SHAR-1192 — departure on the origin's clock, arrival on the destination's.
    { title: 'Departure', key: 'dep', width: 170, render: (_, q) => fmtZoned(q.departureZoned, dayjs(q.departureDateTime).format('DD/MM/YYYY HH:mm')) },
    // SHAR-608 — arrival was missing; only departure was shown.
    { title: 'Arrival', key: 'arr', width: 170, render: (_, q) => (q.arrivalDateTime ? fmtZoned(q.arrivalZoned, dayjs(q.arrivalDateTime).format('DD/MM/YYYY HH:mm')) : '—') },
    // SHAR-1060 - hours and minutes, as the quote form enters them.
    { title: 'Duration', key: 'dur', width: 100, render: (_, q) => formatHoursMinutes(q.flightDurationHours) },
    // SHAR-608 — the money columns: what the operator charges, the margin,
    // the processing fee, the total, what the user already put down and
    // what is left.
    {
      title: 'Operator price',
      key: 'opprice',
      width: 140,
      render: (_, q) =>
        q.operatorPriceAmount != null
          ? `${amount(q.operatorPriceAmount)} ${q.operatorPriceCcy ?? ''}`.trim()
          : '—',
    },
    { title: 'Margin', key: 'margin', width: 110, render: (_, q) => (q.marginGbp != null ? `${gbp(q.marginGbp)}` : '—') },
    { title: COMMISSION_LABEL, key: 'fee', width: 150, render: (_, q) => (q.processingFeeGbp != null ? `${gbp(q.processingFeeGbp)}` : '—') },
    // PO, 29 Sep — the customer's total (hold + balance), as the app shows it.
    { title: 'Total', key: 'price', width: 120, render: (_, q) => <Text strong>{gbp(q.displayTotalGbp ?? q.priceGbp)}</Text> },
    {
      title: 'Pre-auth paid',
      key: 'preauth',
      width: 130,
      render: (_, q) =>
        q.preAuthExpired ? (
          <Tag color="red">Expired</Tag>
        ) : q.preAuthPaidGbp != null ? (
          `${gbp(q.preAuthPaidGbp)}`
        ) : (
          '—'
        ),
    },
    { title: 'Remaining', key: 'remaining', width: 130, render: (_, q) => (q.remainingGbp != null ? `${gbp(q.remainingGbp)}` : '—') },
    // SHAR-640 — with the TIME. A quote expires at a moment, and a
    // column showing only the day cannot tell the admin whether what
    // they set was the moment they meant.
    { title: 'Expiry', key: 'exp', width: 150, render: (_, q) => (q.expiryDate ? dayjs(q.expiryDate).format('DD/MM/YYYY HH:mm') : '—') },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (s: CharterQuote['status'], q) => (
        <Space size={4} wrap>
          <Tag color={CHARTER_QUOTE_STATUS_COLORS[s]}>{statusLabel(CHARTER_QUOTE_STATUS_LABELS, s, q.statusLabel)}</Tag>
          {/* SHAR-115 — "Confirmed by User" is a statement about money:
              it appears only once the full quote price is paid. A booked
              quote awaiting a transfer says so instead, rather than
              implying the customer has settled. */}
          {/* SHAR-780 (retest, 7 Sep) - not on a CANCELLED booking.
              A quote showing "Declined" and "Confirmed by User" at the
              same time reads as a contradiction. Both of these tags
              describe the payment state of a LIVE booking, and a
              cancelled one has no live payment state to report.

              The payment itself is untouched (Mikhail, 7 Sep: only the
              request, quote and booking states change) - it stays on the
              record and in its own section. This is about what the
              status column claims, not about losing the fact. */}
          {/* SHAR-1028 - "Wire transfer required" is an ad-hoc label on the
              charter booking now, removable there; it is not repeated
              as a status on the quote. */}
          {q.booking?.status === 'cancelled' ? null : q.booking?.confirmedByUser ? (
            <Tag color="green">Confirmed by User</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      // SHAR-117 — the quote carries a link to the booking made from it.
      title: 'Booking',
      key: 'booking',
      width: 150,
      render: (_, q) =>
        q.booking ? (
          <Button type="link" style={{ padding: 0 }} onClick={() => nav(`/charter-bookings/${q.booking!.id}`)}>
            {q.booking.reference ?? q.booking.id.slice(-8)}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      key: 'act',
      width: 150,
      fixed: 'right',
      // Regression 29 Sep - a quote with a booking made from it is
      // frozen: deleting it cascaded to the booking, and editing it
      // rewrote what the customer accepted. The API refuses both; the
      // buttons say so instead of offering them.
      render: (_, q) =>
        q.booking ? (
          <Tooltip title="A booking was made from this quote. Edit the charter booking instead.">
            <Space>
              <Button size="small" icon={<EditOutlined />} disabled />
              <Button size="small" danger icon={<DeleteOutlined />} disabled />
            </Space>
          </Tooltip>
        ) : (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(q)} />
            <Popconfirm title="Delete this quote?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => deleteMut.mutate(q.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
    },
  ];
}
